import clsx from 'clsx'
import React, { forwardRef } from 'react'
import FieldError from '../input/FieldParts'

export interface ChoiceOption {
  value: string
  label: string
}

// Choices shown at once instead of hidden in a dropdown (design system
// "Вибір"): radio chips, radio tiles, a segmented control, check chips. The
// inputs stay real (sr-only) radios and checkboxes inside their labels, so Tab,
// arrows and Space work and the label shows the keyboard focus.
const FOCUS =
  'has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-brand'
const CHIP =
  'relative inline-flex items-center gap-1.5 h-10 px-3.5 rounded-full border border-solid text-sm cursor-pointer transition-colors duration-150'
const CHIP_ON = 'border-brand-strong bg-brand-soft text-brand-ink font-extrabold'
const CHIP_OFF = 'border-ink/[0.18] bg-surface text-ink font-bold hover:border-brand'

const VARIANTS = {
  chips: {
    group: 'flex flex-wrap gap-2',
    item: CHIP,
    on: CHIP_ON,
    off: CHIP_OFF,
  },
  // a 2px frame drawn inside, so choosing a tile does not shift the grid
  tiles: {
    group: 'grid grid-cols-2 sm:grid-cols-3 gap-2',
    item: 'relative flex items-center justify-center min-h-[3.25rem] px-2.5 py-1.5 rounded-xl border border-solid text-center text-sm leading-[1.125rem] cursor-pointer transition-colors duration-150',
    on: 'border-brand-strong shadow-[inset_0_0_0_1px_#3c5ef4] bg-brand-soft text-brand-ink font-extrabold',
    off: 'border-ink/[0.18] bg-surface text-ink font-bold hover:border-brand',
  },
  segmented: {
    group: 'flex gap-1 p-1 h-11 rounded-xl bg-track',
    item: 'relative flex flex-1 items-center justify-center px-3 rounded-[0.5625rem] text-sm cursor-pointer transition-colors duration-150',
    on: 'bg-raised font-extrabold text-ink shadow-[0_1px_4px_rgba(33,36,48,0.12)]',
    off: 'font-bold text-muted hover:text-ink',
  },
}

const Legend = ({
  text,
  required,
  className,
}: {
  text: string
  required?: boolean
  className?: string
}) => (
  <legend
    className={clsx('p-0 mb-3 text-base font-extrabold leading-[1.375rem] text-ink', className)}
  >
    {text}
    {required && <span className="ml-0.5 text-danger">*</span>}
  </legend>
)

const Check = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12l5 5 9-10" />
  </svg>
)

interface IRadioChoice {
  legend: string
  name: string
  options: ChoiceOption[]
  value?: ChoiceOption | null
  onChange: (option: ChoiceOption) => void
  variant?: keyof typeof VARIANTS
  required?: boolean
  legendClassName?: string
  // e.g. fixed-width currency segments
  itemClassName?: string
  className?: string
  error?: string
}

export const RadioChoice = forwardRef<HTMLInputElement, IRadioChoice>(
  (
    {
      legend,
      name,
      options,
      value,
      onChange,
      variant = 'chips',
      required,
      legendClassName,
      itemClassName,
      className,
      error,
    },
    ref,
  ) => {
    const look = VARIANTS[variant]
    return (
      <fieldset className={clsx('m-0 p-0 border-0 min-w-0', className)}>
        <Legend text={legend} required={required} className={legendClassName} />
        <div className={look.group}>
          {options.map((option, index) => {
            const checked = value?.value === option.value
            return (
              <label
                key={option.value}
                className={clsx(look.item, FOCUS, checked ? look.on : look.off, itemClassName)}
              >
                <input
                  ref={index === 0 ? ref : undefined}
                  type="radio"
                  name={name}
                  value={option.value}
                  checked={checked}
                  onChange={() => onChange(option)}
                  className="sr-only"
                />
                {option.label}
              </label>
            )
          })}
        </div>
        {error && <FieldError message={error} />}
      </fieldset>
    )
  },
)
RadioChoice.displayName = 'RadioChoice'

interface ICheckChips {
  legend: string
  name: string
  options: ChoiceOption[]
  value?: ChoiceOption[] | null
  onChange: (options: ChoiceOption[]) => void
  disabled?: boolean
  legendClassName?: string
  className?: string
}

// Several answers at once: the options stay in their own order in the value
export const CheckChips = forwardRef<HTMLInputElement, ICheckChips>(
  ({ legend, name, options, value, onChange, disabled, legendClassName, className }, ref) => {
    const chosen = new Set((value || []).map((option) => option.value))
    const toggle = (option: ChoiceOption) =>
      onChange(
        options.filter((entry) =>
          entry.value === option.value ? !chosen.has(entry.value) : chosen.has(entry.value),
        ),
      )
    return (
      <fieldset
        disabled={disabled}
        className={clsx('m-0 p-0 border-0 min-w-0', disabled && 'opacity-50', className)}
      >
        <Legend
          text={legend}
          className={clsx('!mb-2.5 !text-[0.9375rem] !leading-5', legendClassName)}
        />
        <div className="flex flex-wrap gap-2">
          {options.map((option, index) => {
            const checked = chosen.has(option.value)
            return (
              <label key={option.value} className={clsx(CHIP, FOCUS, checked ? CHIP_ON : CHIP_OFF)}>
                <input
                  ref={index === 0 ? ref : undefined}
                  type="checkbox"
                  name={name}
                  value={option.value}
                  checked={checked}
                  onChange={() => toggle(option)}
                  className="sr-only"
                />
                {checked && <Check />}
                {option.label}
              </label>
            )
          })}
        </div>
      </fieldset>
    )
  },
)
CheckChips.displayName = 'CheckChips'

interface IToggleChip {
  label: string
  name?: string
  checked?: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
}

// One yes/no answer drawn as a chip, so it sits in a row with check chips
export const ToggleChip = forwardRef<HTMLInputElement, IToggleChip>(
  ({ label, name, checked = false, onChange, disabled }, ref) => (
    <label
      className={clsx(
        CHIP,
        FOCUS,
        checked ? CHIP_ON : CHIP_OFF,
        disabled && 'pointer-events-none opacity-50',
      )}
    >
      <input
        ref={ref}
        type="checkbox"
        name={name}
        checked={checked}
        disabled={disabled}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.checked)}
        className="sr-only"
      />
      {checked && <Check />}
      {label}
    </label>
  ),
)
ToggleChip.displayName = 'ToggleChip'
